import { useRef, useState, useEffect, useCallback } from 'react';
import { BadgeCheck, MoreHorizontal } from 'lucide-react';
import { useSize } from 'ahooks';
import { debounce } from 'lodash-es';
import { Popover, PopoverContent, PopoverTrigger } from '@leadsnavi/ui-kit';

import type { CompanyCertItem } from '../../../../types';

// ─── 颜色风格定义（来自设计稿 node 9824-52173）────────────────

const CERT_STYLES = [
  // 0 — 蓝色系（如 ISO）
  { border: '#DBEAFE', text: '#1447E6' },
  // 1 — 红色系（如 FSC Certified）
  { border: '#FFE2E2', text: '#E7000B' },
  // 2 — 绿色系（如 BSCI）
  { border: '#DCFCE7', text: '#008236' },
] as const;

/**
 * 根据认证名称计算颜色风格下标（0 | 1 | 2）
 * 使用简单 djb2 hash，保证同一 name 始终映射到相同风格
 */
function getCertStyleIndex(name: string): 0 | 1 | 2 {
  let hash = 5381;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 33) ^ name.charCodeAt(i);
  }
  return (Math.abs(hash) % CERT_STYLES.length) as 0 | 1 | 2;
}

// ─── CertBadge ───────────────────────────────────────────────

interface CertBadgeProps {
  cert: CompanyCertItem;
}

function CertBadge({ cert }: CertBadgeProps) {
  const styleIndex = getCertStyleIndex(cert.name);
  const { border, text } = CERT_STYLES[styleIndex];

  return (
    <span
      className="inline-flex items-center gap-1 rounded px-1.5 h-5 text-xs font-medium leading-[150%] tracking-[0.015em] whitespace-nowrap [font-family:Geist]"
      style={{ border: `1px solid ${border}`, color: text }}
    >
      {cert.image && <img src={cert.image} alt="" className="w-2.5 h-2.5 object-contain shrink-0" />}
      {cert.name}
    </span>
  );
}

// ─── VerifyBadge ─────────────────────────────────────────────

export function VerifyBadge() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded px-1.5 h-5 text-xs font-medium leading-[150%] tracking-[0.015em] whitespace-nowrap [font-family:Geist]"
      style={{ background: 'rgba(55, 228, 166, 0.08)', border: '1px solid #A7F3D0', color: '#065F46' }}
    >
      <BadgeCheck size={12} color="#065F46" strokeWidth={2} className="shrink-0" />
      Verify
    </span>
  );
}

// ─── MoreButton ──────────────────────────────────────────────

function MoreButton() {
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded cursor-pointer bg-[#F8F8F7] hover:bg-[#E5E5E5] transition-colors shrink-0">
      <MoreHorizontal size={12} color="#525252" strokeWidth={2} />
    </span>
  );
}

// ─── CertBlock ───────────────────────────────────────────────

interface CertBlockProps {
  /** show=false 时整个区块不渲染 */
  show: boolean;
  certList: CompanyCertItem[] | null | undefined;
  /** 是否展示 Verify 徽章，默认 true */
  showVerify?: boolean;
}

/** 每个 badge 预估最大宽度（px），用于 ResizeObserver 无法精确量时的保底策略 */
const MORE_BTN_WIDTH = 24; // ··· 按钮宽度 + gap
const GAP = 4; // gap-1
const DEBOUNCE_DELAY = 100; // ms

export function CertBlock({ show, certList, showVerify = true }: CertBlockProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [hiddenCount, setHiddenCount] = useState(0);

  // useSize 监听容器宽度变化
  const containerSize = useSize(containerRef);

  /**
   * 遍历测量层子元素，计算哪些 badge 需要折叠进 ··· popover
   * 从第一个超出容器宽度的元素开始，后续全部收入 hiddenCount
   */
  const computeHiddenCount = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const containerWidth = container.offsetWidth;
    if (containerWidth === 0) return;

    const children = Array.from(measure.children) as HTMLElement[];
    if (children.length === 0) return;

    let usedWidth = 0;
    let overflowIndex = -1;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const isLast = i === children.length - 1;
      // 若后续还有元素，需为 ··· 按钮预留空间
      const needReserve = i < children.length - 1;
      const totalNeeded = usedWidth + (i > 0 ? GAP : 0) + childWidth + (needReserve ? GAP + MORE_BTN_WIDTH : 0);

      if (totalNeeded > containerWidth && !isLast) {
        overflowIndex = i;
        break;
      }
      usedWidth += (i > 0 ? GAP : 0) + childWidth;
    }

    setHiddenCount(overflowIndex === -1 ? 0 : children.length - overflowIndex);
  }, []);

  useEffect(() => {
    const debouncedCompute = debounce(computeHiddenCount, DEBOUNCE_DELAY);
    debouncedCompute();
    return () => debouncedCompute.cancel();
  }, [certList, showVerify, containerSize?.width, computeHiddenCount]);

  if (!show) return null;

  const allBadges: React.ReactNode[] = [];
  if (showVerify) allBadges.push(<VerifyBadge key="verify" />);
  certList?.forEach((cert, i) => allBadges.push(<CertBadge key={i} cert={cert} />));

  const visibleCount = allBadges.length - hiddenCount;
  const visibleBadges = allBadges.slice(0, visibleCount);
  const hiddenBadges = allBadges.slice(visibleCount);

  return (
    <div className="mt-2 relative">
      {/* 隐形测量层：渲染所有 badge，用于计算各子元素宽度 */}
      <div ref={measureRef} className="absolute top-0 left-0 flex gap-1 invisible pointer-events-none" aria-hidden>
        {allBadges}
      </div>

      {/* 可见层 */}
      <div ref={containerRef} className="flex items-center gap-1 h-5 overflow-hidden flex-nowrap w-full">
        {visibleBadges}

        {hiddenBadges.length > 0 && (
          <Popover trigger="hover">
            <PopoverTrigger asChild>
              <span className="inline-flex shrink-0">
                <MoreButton />
              </span>
            </PopoverTrigger>
            <PopoverContent
              side="bottom"
              align="start"
              sideOffset={6}
              className="p-3 rounded-xl border border-[rgba(134,146,166,0.2)] shadow-[0px_4px_24px_0px_rgba(0,0,0,0.04)] w-auto max-w-[240px]"
            >
              <div className="flex flex-wrap gap-1">{hiddenBadges}</div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
}
